/* Product data model shared by the cards, the sheet, the PDP and the lander.
   Reads the JSON the theme renders per product (snippets/product-json.liquid):
   { id, handle, title, sub, url, unit, options:[{name, values:[]}], packIndex, colorIndex, variants:[{id, price, compare, available, options:[], qty, image, sku}] } */
import { money, fmt, parseQty } from '@theme/utils';

const T = () => window.Theme || { settings: {}, strings: {} };

export class ProductModel {
  constructor(data) {
    this.d = data;
    this.options = data.options || [];
    this.packIndex = typeof data.packIndex === 'number' ? data.packIndex : -1;
    this.colorIndex = typeof data.colorIndex === 'number' ? data.colorIndex : -1;
    this.unit = data.unit || T().settings.unitNoun || 'pair';
    this.variants = (data.variants || []).map((v) => ({ ...v, qty: v.qty || (this.packIndex >= 0 ? parseQty(v.options[this.packIndex]) : 1) }));
    this.defaultQty = Number(T().settings.defaultPackQty) || 2;
    /* "Ships free" goes on the smallest pack from 2 up to this quantity (theme settings, default 3) whose price clears the
       free-shipping threshold; no "Most chosen" tag any more (27 October 2026: the single is the most chosen rung nearly everywhere) */
    this.freeTagMax = Number(T().settings.shipsFreeTagMax) || 3;
    this.crewQty = Number(T().settings.crewPackQty) || 5;
    /* "Buy X, get Y" shows when the saving is within this share of a unit of a whole number of free units (theme settings, default 10%) */
    this.bogoTol = (Number.isFinite(Number(T().settings.bogoTolerance)) ? Number(T().settings.bogoTolerance) : 10) / 100;
    this.freeCents = Number(T().settings.freeShippingCents) || 7000;
  }
  /** Options other than the pack option, that have more than one value. */
  get pickOptions() {
    return this.options.map((o, i) => ({ ...o, index: i })).filter((o) => o.index !== this.packIndex && o.values.length > 1);
  }
  get colorOption() { return this.colorIndex >= 0 ? { ...this.options[this.colorIndex], index: this.colorIndex } : null; }
  get sizeOptions() { return this.pickOptions.filter((o) => o.index !== this.colorIndex); }
  /** Variants matching a partial selection (array indexed by option position; null = any). */
  match(sel) {
    return this.variants.filter((v) => v.options.every((val, i) => sel[i] == null || sel[i] === val));
  }
  /** The ladder for a selection of non-pack options: one row per pack quantity (unselected options: the first available variant of that quantity). */
  ladder(sel) {
    const base = sel.slice(); if (this.packIndex >= 0) base[this.packIndex] = null;
    const seen = new Map();
    this.match(base).forEach((v) => {
      const cur = seen.get(v.qty);
      if (!cur || (!cur.available && v.available)) seen.set(v.qty, { variant: v, qty: v.qty, price: v.price, available: v.available });
    });
    const rows = Array.from(seen.values());
    rows.sort((a, b) => a.qty - b.qty);
    const single = rows.find((r) => r.qty === 1);
    const unit = single ? single.price : (rows[0] ? rows[0].price / rows[0].qty : 0);
    const maxQty = rows.reduce((m, r) => Math.max(m, r.qty), 0);
    /* the rung tagged "Ships free": the smallest pack from 2 up to freeTagMax that clears the threshold; none when the single does */
    const singleFree = !!single && single.price >= this.freeCents;
    const freeTagQty = singleFree ? 0 : rows.filter((r) => r.qty > 1 && r.qty <= this.freeTagMax && r.price >= this.freeCents).reduce((m, r) => (m === 0 || r.qty < m ? r.qty : m), 0);
    return rows.map((r) => {
      const per = r.price / r.qty;
      const full = unit * r.qty;
      const save = Math.max(0, Math.round(full - r.price));
      const freeUnits = unit > 0 ? Math.round(save / unit) : 0;
      const bogo = freeUnits >= 1 && Math.abs(save - freeUnits * unit) <= Math.max(2, unit * this.bogoTol);
      /* the tags, in order: Buy X get Y; Ships free (tagged: the first pack that clears the threshold); Crew pack; Best value
         (the largest pack). mostChosen only marks the default pack: the product page and the quick-add sheet fall back to it. */
      return { ...r, per, full, save, unit, bogo: bogo ? { buy: r.qty - freeUnits, get: freeUnits } : null, free: r.price >= this.freeCents, mostChosen: r.qty === this.defaultQty && r.qty > 1, tagged: r.qty === freeTagQty && r.qty > 1, crewPack: r.qty === this.crewQty && r.qty > 1, bestValue: rows.length > 1 && r.qty === maxQty && r.qty > this.crewQty && r.qty > 1 };
    });
  }
  /** Meta line for a rung: "$38.63 each · save $6.72 · Free shipping". */
  meta(row) {
    const s = T().strings;
    if (row.qty === 1) return '';
    const parts = [money(Math.round(row.per)) + ' ' + (s.each || 'each')];
    if (row.save > 0) parts.push('<b>' + fmt(s.save || 'save [amount]', { amount: money(row.save) }) + '</b>');
    /* the free-shipping note carries its own separator: the kit hides it in the two-column ladder on desktop */
    return parts.join(' · ') + (row.free && !this.shipsFreeTag(row) ? ' <em class="fs">· ' + (s.freeShipping || 'Free shipping') + '</em>' : '');
  }
  /** The tagged pack reads "Ships free" (a Buy X get Y tag wins); the meta line then leaves its free-shipping note out. */
  shipsFreeTag(row) { return !row.bogo && !!row.tagged && !!row.free; }
  tag(row) {
    const s = T().strings;
    if (row.bogo) return fmt(s.buyXgetY || 'Buy [buy], get [get]', { buy: row.bogo.buy, get: row.bogo.get });
    if (row.tagged && row.free) return s.shipsFree || 'Ships free';
    if (row.crewPack) return s.crewPack || 'Crew pack';
    if (row.bestValue) return s.bestValue || 'Best value';
    return '';
  }
  /** The smallest pack from 2 up that ships free, for the card's pack line. */
  packLine(sel) {
    const rows = this.ladder(sel || this.variants[0].options.map(() => null));
    const pick = rows.find((r) => r.qty >= 2 && r.free) || rows.find((r) => r.qty === 2);
    if (!pick) return '';
    /* the locale's pack line (products.pack_line / pack_line_free, as product-card.liquid prints it) */
    const s = T().strings, price = money(Math.round(pick.per));
    if (s.packLine && s.packLineFree) return fmt(pick.free ? s.packLineFree : s.packLine, { qty: pick.qty, price });
    return pick.qty + '-pack ' + price + ' ' + (s.each || 'each') + (pick.free ? ' · ' + (s.freeShipping || 'free shipping').toLowerCase() : '');
  }
  /** The variant for a selection. A complete selection matches one variant; a partial one (size not chosen yet)
      resolves to the first match that is in stock, so the page never reads "Sold out" while another size is available. */
  find(sel) { const m = this.match(sel); return m.find((v) => v.available) || m[0] || null; }
  byId(id) { return this.variants.find((v) => String(v.id) === String(id)) || null; }
  /** Resolve ?variant= values: a numeric id, or 5PACK / 5 meaning the rung with quantity 5. */
  fromParam(value) {
    if (!value) return null;
    const v = this.byId(value); if (v) return v;
    const m = /^(\d+)\s*(pack|pk)?$/i.exec(String(value).trim());
    if (m && this.packIndex >= 0) {
      const qty = parseInt(m[1], 10);
      return this.variants.find((x) => x.qty === qty && x.available) || this.variants.find((x) => x.qty === qty) || null;
    }
    return null;
  }
}

export function readProductJSON(root) {
  const el = root && root.querySelector('script[type="application/json"][data-product-json]');
  if (!el) return null;
  try { return new ProductModel(JSON.parse(el.textContent)); } catch (e) { return null; }
}
